import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Download,
  Languages,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Wand2,
  Pencil,
  Save,
  X,
  Users,
  FileText,
  FileType2,
  Film,
  Volume2,
} from "lucide-react";
import { toast } from "sonner";

const LANGUAGES = [
  { code: "en", name: "English", script: "", short: "EN" },
  { code: "ml", name: "Malayalam", script: "script-ml", short: "ML" },
  { code: "ta", name: "Tamil", script: "script-ta", short: "TA" },
  { code: "te", name: "Telugu", script: "script-te", short: "TE" },
  { code: "hi", name: "Hindi", script: "script-hi", short: "HI" },
  { code: "tanglish", name: "Tanglish (Spoken Tamil, Tamil script)", script: "script-ta", short: "TG" },
];

const MODELS = [
  { key: "gpt-5.2", name: "GPT-5.2 (OpenAI)" },
  { key: "claude-sonnet-4-6", name: "Claude Sonnet 4.6" },
  { key: "gemini-3-flash", name: "Gemini 3 Flash" },
];

const PROMPT_PRESETS = [
  {
    label: "Google Translate — accurate & neutral",
    value:
      "Translate accurately, preserving meaning and grammatical correctness. Use standard, neutral written form of the target language (as Google Translate would). Do not paraphrase, do not add commentary, do not shift register.",
  },
  {
    label: "Tanglish — spoken Tamil in Tamil script",
    value:
      "Output must be spoken-style Tamil written in Tamil script (தமிழ் எழுத்து). WhatsApp/daily-conversation register — casual, contracted, code-mixed. Do NOT use Latin/Roman letters. Do NOT use formal/literary Tamil. Keep English loanwords as Tamil transliteration in Tamil script (e.g., ஃப்ரெண்ட்ஸ், மார்க்கெட்).",
    forceTarget: "tanglish",
  },
  {
    label: "Colloquial Tamil + transliteration",
    value:
      "Use natural colloquial spoken Tamil (as used in daily Chennai/Coimbatore conversation). Avoid formal literary or Sanskritised vocabulary. For each segment, first give the Tamil translation, then a new line starting with '[translit]' containing the romanised phonetic form. Example: 'let me speak' -> 'என்னை பேச விடு\\n[translit] ennai pesa vidu'.",
  },
  {
    label: "Colloquial Malayalam + translit",
    value:
      "Use natural colloquial spoken Malayalam. Avoid Sanskritised vocabulary. For each segment, give the Malayalam translation and a new line '[translit] <romanised>'.",
  },
  {
    label: "Colloquial Hindi + translit",
    value:
      "Use everyday spoken Hindi (Hindustani), not formal/literary. For each segment give the Devanagari text and a new line '[translit] <romanised>'.",
  },
  {
    label: "Formal / broadcast",
    value:
      "Use formal broadcast-quality register. Correct grammar. No slang.",
  },
];

function fmtTime(s) {
  s = Math.max(0, s || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.floor((s - Math.floor(s)) * 1000);
  return `${h > 0 ? String(h).padStart(2, "0") + ":" : ""}${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

export default function VideoEditor() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("transcribe");

  // Transcribe
  const [transcriptionPrompt, setTranscriptionPrompt] = useState("");
  const [turbo, setTurbo] = useState(false);
  const [retranscribing, setRetranscribing] = useState(false);

  // Translate
  const [displayLang, setDisplayLang] = useState("original");
  const [targetLang, setTargetLang] = useState("ta");
  const [model, setModel] = useState("gpt-5.2");
  const [translationPrompt, setTranslationPrompt] = useState(PROMPT_PRESETS[0].value);
  const [translating, setTranslating] = useState(false);
  const [lastTranslation, setLastTranslation] = useState(null); // {lang, model, segments}
  const [refining, setRefining] = useState(false);
  const [refineStats, setRefineStats] = useState(null); // {scope, refined_count, remaining_flagged, target_lang?}
  const [reviewMode, setReviewMode] = useState(false); // walk-through modal
  const [syncFixing, setSyncFixing] = useState(false);

  // Analysis / edit
  const [analyzing, setAnalyzing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editBuffer, setEditBuffer] = useState([]);
  const [savingEdits, setSavingEdits] = useState(false);
  const [rebuildingProxy, setRebuildingProxy] = useState(false);
  const [bilingualView, setBilingualView] = useState(false); // side-by-side toggle
  const [flaggedOnly, setFlaggedOnly] = useState(false); // "needs review" filter
  const [retranscribeSegIdx, setRetranscribeSegIdx] = useState(null); // segment currently being re-transcribed

  // Export
  const [exportPrimary, setExportPrimary] = useState("original");
  const [exportSecondary, setExportSecondary] = useState("");
  const [exportLayout, setExportLayout] = useState("inline");
  const [includeSpeaker, setIncludeSpeaker] = useState(true);
  const [exportFresh, setExportFresh] = useState(true);

  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef(null);
  const listRef = useRef(null);

  const fetchVideo = useCallback(async () => {
    try {
      const { data } = await api.get(`/videos/${id}`);
      setVideo(data);
      setTranscriptionPrompt((prev) => (prev ? prev : data?.prompt || ""));
      setTurbo((prev) => (prev !== undefined && prev !== null ? prev : !!data?.turbo));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVideo();
  }, [fetchVideo]);

  useEffect(() => {
    if (!video) return;
    if (!["queued", "extracting", "transcribing"].includes(video.status)) return;
    const t = setInterval(fetchVideo, 3500);
    return () => clearInterval(t);
  }, [video, fetchVideo]);

  const streamUrl = useMemo(() => {
    if (!video) return null;
    const token = localStorage.getItem("vt_token") || "";
    return `${API_BASE}/videos/${video.id}/stream?token=${encodeURIComponent(token)}`;
  }, [video]);

  const activeSegments = useMemo(() => {
    if (!video) return [];
    if (displayLang === "original" || displayLang === (video.language || "en")) {
      return video.segments || [];
    }
    return video.translations?.[displayLang]?.segments || [];
  }, [video, displayLang]);

  const isTranslatedView =
    !!video &&
    displayLang !== "original" &&
    displayLang !== (video.language || "en");

  // Compute silence gaps (leading + inter-segment) to render as visual markers.
  // Only reported when the gap is >= 1.5s so we don't clutter with sub-second seams.
  const SILENCE_MIN = 1.5;
  const silenceMap = useMemo(() => {
    const map = {}; // { beforeIndex: seconds }
    if (!activeSegments.length) return map;
    if (activeSegments[0].start >= SILENCE_MIN) {
      map[0] = activeSegments[0].start;
    }
    for (let i = 1; i < activeSegments.length; i++) {
      const gap = activeSegments[i].start - activeSegments[i - 1].end;
      if (gap >= SILENCE_MIN) map[i] = gap;
    }
    return map;
  }, [activeSegments]);

  const reviewStats = useMemo(() => {
    if (!isTranslatedView) return { flagged: 0, total: 0, hasScores: false };
    const flagged = activeSegments.reduce(
      (n, s) => n + (s?.needs_review ? 1 : 0),
      0
    );
    const hasScores = activeSegments.some((s) => typeof s?.confidence === "number");
    return { flagged, total: activeSegments.length, hasScores };
  }, [activeSegments, isTranslatedView]);

  const activeIndex = useMemo(() => {
    if (!activeSegments.length) return -1;
    // Exact match — currentTime is inside a segment
    const direct = activeSegments.findIndex(
      (s) => currentTime >= s.start && currentTime <= s.end
    );
    if (direct !== -1) return direct;
    // Silent gap — hold the highlight on the most-recently-played segment so
    // the UI doesn't blank out during no-voice sections. If we're before any
    // segment, return -1.
    let held = -1;
    for (let i = 0; i < activeSegments.length; i++) {
      if (activeSegments[i].start <= currentTime) held = i;
      else break;
    }
    return held;
  }, [activeSegments, currentTime]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-seg-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeIndex]);

  const runSyncFix = async () => {
    if (!video || syncFixing) return;
    setSyncFixing(true);
    try {
      await api.post(`/videos/${video.id}/sync-fix`);
      toast.success("Rebuilding playback and re-transcribing — this runs in the background");
      // fetchVideo polling in useEffect will pick up the status changes
      await fetchVideo();
    } catch (err) {
      toast.error(formatApiError(err));
      setSyncFixing(false);
    }
  };

  // Reset the syncFixing spinner when the backend reports the repair is done
  useEffect(() => {
    if (syncFixing && video?.status === "done" && video?.sync_normalized) {
      setSyncFixing(false);
      toast.success("Sync repair complete — transcript is now aligned to playback");
    }
    if (syncFixing && video?.status === "failed") {
      setSyncFixing(false);
    }
  }, [video?.status, video?.sync_normalized, syncFixing]);

  const jumpTo = (t) => {
    if (videoRef.current) {
      videoRef.current.currentTime = t;
      videoRef.current.play().catch(() => {});
    }
  };

  const scrollToActive = useCallback(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-seg-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeIndex]);

  const retranscribeSegment = async (idx) => {
    if (!video || retranscribeSegIdx != null) return;
    setRetranscribeSegIdx(idx);
    try {
      const { data } = await api.post(`/videos/${video.id}/segments/${idx}/retranscribe`, {
        prompt: transcriptionPrompt.trim() || null,
      });
      toast.success(`Segment #${idx + 1} re-transcribed`);
      // Optimistically patch the video state before refetching
      setVideo((prev) => {
        if (!prev) return prev;
        const nextSegs = [...(prev.segments || [])];
        if (data?.segment && idx < nextSegs.length) nextSegs[idx] = data.segment;
        return { ...prev, segments: nextSegs };
      });
      await fetchVideo();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRetranscribeSegIdx(null);
    }
  };

  const runTranslate = async () => {
    if (!video) return;
    setTranslating(true);
    setRefineStats(null);
    try {
      const { data } = await api.post(`/videos/${video.id}/translate`, {
        target_lang: targetLang,
        model,
        custom_prompt: translationPrompt.trim() || null,
        auto_refine: true,
      });
      setLastTranslation({
        lang: data?.target_lang || targetLang,
        model: data?.model || model,
        segments: Array.isArray(data?.segments) ? data.segments : [],
      });
      if (typeof data?.initial_flagged === "number") {
        setRefineStats({
          scope: "translation",
          target_lang: data?.target_lang || targetLang,
          initial_flagged: data.initial_flagged,
          refined_count: data.refined_count || 0,
          remaining_flagged: data.remaining_flagged || 0,
        });
      }
      const refineNote =
        data?.refined_count > 0
          ? ` · auto-refined ${data.refined_count}`
          : "";
      toast.success(
        `Translation complete — ${(data?.segments?.length ?? 0)} segments in ${targetLang.toUpperCase()}${refineNote}`
      );
      await fetchVideo();
      setDisplayLang(targetLang);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTranslating(false);
    }
  };

  const runRefine = async ({ scope }) => {
    if (!video) return;
    // scope: "transcription" | "translation"
    if (scope === "translation" && !isTranslatedView) {
      toast.error("Switch to a translated language before refining.");
      return;
    }
    setRefining(true);
    try {
      const body = {
        model,
        custom_prompt: null,
        only_flagged: true,
        target_lang: scope === "transcription" ? null : displayLang,
      };
      const { data } = await api.post(`/videos/${video.id}/refine`, body);
      setRefineStats({
        scope,
        target_lang: data?.target_lang || null,
        refined_count: data?.refined_count || 0,
        remaining_flagged: data?.remaining_flagged || 0,
      });
      const label = scope === "transcription" ? "transcript" : displayLang.toUpperCase();
      toast.success(
        `${label} refined — improved ${data?.refined_count || 0}, ${data?.remaining_flagged || 0} still flagged`
      );
      await fetchVideo();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefining(false);
    }
  };

  const runRetranscribe = async () => {
    if (!video) return;
    if (!window.confirm("Re-transcribe with the current hint? Existing translations will be discarded.")) return;
    setRetranscribing(true);
    try {
      await api.post(`/videos/${video.id}/retranscribe`, {
        prompt: transcriptionPrompt.trim() || null,
        turbo,
      });
      toast.success(turbo ? "Turbo re-transcription started" : "Re-transcription started");
      await fetchVideo();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRetranscribing(false);
    }
  };

  const rebuildProxy = async () => {
    if (!video) return;
    setRebuildingProxy(true);
    try {
      await api.post(`/videos/${video.id}/rebuild-proxy`);
      toast.success("Rebuilding playback file — this takes a few seconds");
      // Poll for proxy_path change
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await api.get(`/videos/${video.id}`);
        if (data.proxy_path) {
          setVideo(data);
          toast.success("Playback file ready — reloading player");
          if (videoRef.current) {
            videoRef.current.load();
          }
          break;
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRebuildingProxy(false);
    }
  };

  const runAnalyze = async () => {
    if (!video) return;
    setAnalyzing(true);
    try {
      await api.post(`/videos/${video.id}/analyze`, { model });
      toast.success("Speakers & languages detected");
      await fetchVideo();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const startEdit = () => {
    setEditBuffer(activeSegments.map((s) => ({ ...s })));
    setEditMode(true);
  };
  const cancelEdit = () => {
    setEditMode(false);
    setEditBuffer([]);
  };
  const updateBufferText = (idx, text) => {
    setEditBuffer((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], text };
      return next;
    });
  };
  const saveEdits = async () => {
    if (!video) return;
    setSavingEdits(true);
    try {
      await api.put(`/videos/${video.id}/segments`, {
        lang: displayLang,
        segments: editBuffer.map((s) => ({
          start: s.start,
          end: s.end,
          text: s.text,
          speaker: s.speaker || null,
          lang: s.lang || null,
        })),
      });
      toast.success("Changes saved");
      await fetchVideo();
      setEditMode(false);
      setEditBuffer([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEdits(false);
    }
  };

  const downloadFile = (format, primary, secondary) => {
    if (!video) return;
    const token = localStorage.getItem("vt_token") || "";
    const path = format === "srt" ? "srt" : "export";
    const params = new URLSearchParams({
      lang: primary,
      include_speaker: String(includeSpeaker),
      token,
    });
    if (format !== "srt") params.set("format", format);
    if (format === "srt") params.set("layout", exportLayout);
    if (secondary) params.set("secondary_lang", secondary);
    if (exportFresh) params.set("fresh", "1");
    const url = `${API_BASE}/videos/${video.id}/${path}?${params.toString()}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="p-14 text-center text-muted-foreground font-mono animate-pulse">
          LOADING SESSION…
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="p-14 text-center">
          <div className="text-2xl font-black tracking-tighter">Session not found</div>
          <Link to="/" className="text-primary text-sm mt-4 inline-block">← Back to studio</Link>
        </div>
      </div>
    );
  }

  const isDone = video.status === "done";
  const isFailed = video.status === "failed";
  const canRetry = isDone || isFailed;
  const scriptClass = LANGUAGES.find((l) => l.code === displayLang)?.script || "";
  const availableTranslations = Object.keys(video.translations || {});

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        {/* Session Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <Link
              to="/"
              data-testid="back-to-studio"
              className="inline-flex items-center gap-1.5 text-xs font-mono tracking-widest text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-3 h-3" /> BACK TO STUDIO
            </Link>
            <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tighter truncate max-w-2xl" data-testid="video-title">
              {video.filename}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-mono text-muted-foreground">
              <span>{new Date(video.created_at).toLocaleString()}</span>
              {video.language && (
                <>
                  <span>·</span>
                  <span className="uppercase text-primary">DETECTED: {video.language}</span>
                </>
              )}
              {video.duration > 0 && (
                <>
                  <span>·</span>
                  <span>{fmtTime(video.duration)}</span>
                </>
              )}
              {availableTranslations.length > 0 && (
                <>
                  <span>·</span>
                  <span className="uppercase">TRANSLATIONS: {availableTranslations.join(", ").toUpperCase()}</span>
                </>
              )}
            </div>
          </div>
          <StatusBar
            status={video.status}
            progress={video.progress || 0}
            error={video.error}
            stageStartedAt={video.stage_started_at}
            onCancel={async () => {
              try {
                await api.post(`/videos/${video.id}/cancel`);
                toast.success("Cancelled — click Re-transcribe to try again");
                await fetchVideo();
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
          />
        </div>

        {/* Sync drift detector — shown when transcript was extracted before the
            timestamp-normalisation fix. Offers a one-click repair. */}
        {isDone && !video.sync_normalized && (video.segments?.length || 0) > 0 && (
          <div
            data-testid="sync-drift-banner"
            className="mb-5 flex flex-wrap items-center gap-3 border border-amber-400/50 bg-amber-400/10 p-3 text-sm"
          >
            <AlertCircle className="w-4 h-4 text-amber-300 shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] font-mono tracking-[0.25em] text-amber-300 mb-0.5">
                POSSIBLE SYNC DRIFT
              </div>
              <div className="text-xs text-amber-100/90 leading-relaxed">
                This video was processed before the timestamp-normalisation fix.
                The transcript timeline may not exactly match playback. One click
                below will rebuild the video + audio and re-transcribe — usually
                a couple of minutes.
              </div>
            </div>
            <Button
              size="sm"
              onClick={runSyncFix}
              disabled={syncFixing}
              data-testid="sync-drift-fix-button"
              className="rounded-none bg-amber-400 text-black hover:bg-amber-300 font-semibold"
            >
              {syncFixing ? (
                <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Rebuilding…</>
              ) : (
                <>Rebuild &amp; Re-transcribe →</>
              )}
            </Button>
          </div>
        )}

        {/* Main grid */}
        <div className="grid lg:grid-cols-12 gap-5">
          {/* LEFT: 3-step tabs */}
          <div className="lg:col-span-7">
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList
                className="grid grid-cols-4 rounded-none bg-transparent p-0 border-b border-white/10 h-auto"
                data-testid="editor-tabs"
              >
                <TabsTrigger
                  value="transcribe"
                  data-testid="tab-transcribe"
                  className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary text-muted-foreground font-mono text-[11px] tracking-[0.2em] py-3"
                >
                  1 · TRANSCRIBE
                </TabsTrigger>
                <TabsTrigger
                  value="translate"
                  disabled={!isDone}
                  data-testid="tab-translate"
                  className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary text-muted-foreground font-mono text-[11px] tracking-[0.2em] py-3 disabled:opacity-30"
                >
                  2 · TRANSLATE
                </TabsTrigger>
                <TabsTrigger
                  value="dub"
                  disabled={!isDone}
                  data-testid="tab-dub"
                  className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary text-muted-foreground font-mono text-[11px] tracking-[0.2em] py-3 disabled:opacity-30"
                >
                  3 · DUB
                </TabsTrigger>
                <TabsTrigger
                  value="export"
                  disabled={!isDone}
                  data-testid="tab-export"
                  className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary text-muted-foreground font-mono text-[11px] tracking-[0.2em] py-3 disabled:opacity-30"
                >
                  4 · EXPORT
                </TabsTrigger>
              </TabsList>

              {/* TAB 1: Transcribe */}
              <TabsContent value="transcribe" className="mt-5 space-y-5" data-testid="panel-transcribe">
                <div className="card-surface overflow-hidden">
                  {streamUrl ? (
                    <video
                      ref={videoRef}
                      data-testid="video-player"
                      controls
                      preload="metadata"
                      playsInline
                      src={streamUrl}
                      onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                      className="w-full aspect-video bg-black"
                    >
                      Your browser can&apos;t play this video.
                    </video>
                  ) : (
                    <div className="aspect-video bg-black" />
                  )}
                  <div className="px-3 py-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono tracking-widest text-muted-foreground">
                    <span>
                      {video.proxy_path ? "PLAYING · WEB PROXY (H.264/AAC)" : "PLAYING · ORIGINAL FILE"}
                    </span>
                    <button
                      onClick={rebuildProxy}
                      disabled={rebuildingProxy || !video.video_path}
                      data-testid="rebuild-proxy-button"
                      className="px-2 py-1 border border-white/10 hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {rebuildingProxy ? "REBUILDING…" : "REBUILD PLAYBACK"}
                    </button>
                  </div>
                </div>

                <div className="card-surface p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Wand2 className="w-4 h-4 text-primary" />
                    <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground">
                      TRANSCRIPTION HINT · OPTIONAL
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Guide Whisper with names, jargon, or context to improve accuracy — then re-run transcription without re-uploading.
                  </p>
                  <Textarea
                    value={transcriptionPrompt}
                    onChange={(e) => setTranscriptionPrompt(e.target.value)}
                    disabled={retranscribing || !canRetry}
                    data-testid="retranscribe-prompt"
                    rows={2}
                    maxLength={800}
                    placeholder="e.g., Tamil film dialogue with English code-switching. Characters: Anu, Kusuma, Ravi."
                    className="rounded-none border-white/10 bg-secondary/60 resize-y"
                  />
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <label
                      htmlFor="editor-turbo"
                      className={`flex items-center gap-2 cursor-pointer text-[10px] font-mono tracking-widest px-2 py-1 border transition-colors ${
                        turbo
                          ? "border-primary/60 bg-primary/10 text-primary"
                          : "border-white/10 text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="editor-turbo-label"
                    >
                      <input
                        id="editor-turbo"
                        type="checkbox"
                        checked={turbo}
                        onChange={(e) => setTurbo(e.target.checked)}
                        disabled={retranscribing}
                        data-testid="editor-turbo-toggle"
                        className="w-3 h-3 accent-primary"
                      />
                      ⚡ TURBO {turbo && "· 8× PARALLEL"}
                    </label>
                    <div className="text-[10px] font-mono tracking-widest text-muted-foreground">
                      {transcriptionPrompt.length}/800
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={runAnalyze}
                        disabled={!isDone || analyzing}
                        data-testid="analyze-button"
                        className="rounded-none h-9 border-white/15 hover:border-primary/50 hover:text-primary"
                      >
                        {analyzing ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Analyzing</>
                        ) : (
                          <><Users className="w-3.5 h-3.5 mr-2" />Detect speakers</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => runRefine({ scope: "transcription" })}
                        disabled={!isDone || refining}
                        data-testid="refine-transcription-button"
                        className="rounded-none h-9 border-white/15 hover:border-primary/50 hover:text-primary"
                      >
                        {refining ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Refining</>
                        ) : (
                          <><Wand2 className="w-3.5 h-3.5 mr-2" />AI Refine</>
                        )}
                      </Button>
                      <Button
                        onClick={runRetranscribe}
                        disabled={!canRetry || retranscribing}
                        data-testid="retranscribe-button"
                        className="rounded-none h-9 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                      >
                        {retranscribing ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Re-transcribing</>
                        ) : isFailed ? (
                          <><Wand2 className="w-3.5 h-3.5 mr-2" />Retry transcription</>
                        ) : (
                          <><Wand2 className="w-3.5 h-3.5 mr-2" />Re-transcribe</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TAB 2: Translate */}
              <TabsContent value="translate" className="mt-5 space-y-5" data-testid="panel-translate">
                <div className="card-surface p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Languages className="w-4 h-4 text-primary" />
                    <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground">
                      TRANSLATE INTO A LANGUAGE
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1.5">
                        TARGET
                      </div>
                      <Select value={targetLang} onValueChange={setTargetLang}>
                        <SelectTrigger data-testid="translate-target-lang" className="rounded-none border-white/10 bg-secondary/60 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES.map((l) => (
                            <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1.5">
                        MODEL
                      </div>
                      <Select value={model} onValueChange={setModel}>
                        <SelectTrigger data-testid="translate-model" className="rounded-none border-white/10 bg-secondary/60 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODELS.map((m) => (
                            <SelectItem key={m.key} value={m.key}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1.5">
                      STYLE PRESET · TAP TO INSERT
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {PROMPT_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            setTranslationPrompt(preset.value);
                            if (preset.forceTarget) setTargetLang(preset.forceTarget);
                          }}
                          disabled={translating}
                          data-testid={`preset-${preset.label.replace(/\s+/g, "-").toLowerCase()}`}
                          className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40"
                        >
                          {preset.label.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <Textarea
                      value={translationPrompt}
                      onChange={(e) => setTranslationPrompt(e.target.value)}
                      disabled={translating}
                      data-testid="translate-prompt"
                      rows={4}
                      maxLength={1000}
                      placeholder="Custom instructions for the translator — dialect, register, transliteration, etc. These are sent to the LLM alongside every segment."
                      className="rounded-none border-white/10 bg-secondary/60 resize-y"
                    />
                    <div className="mt-1 flex items-center justify-between gap-3 text-[10px] font-mono tracking-widest text-muted-foreground">
                      <span>
                        {translationPrompt.trim()
                          ? "PROMPT WILL BE APPLIED TO THIS TRANSLATION"
                          : "NO CUSTOM PROMPT — DEFAULT NEUTRAL STYLE"}
                      </span>
                      <span>{translationPrompt.length}/1000</span>
                    </div>

                    <Button
                      onClick={runTranslate}
                      disabled={translating}
                      data-testid="translate-button"
                      className="mt-4 w-full rounded-none h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-sm tracking-wide"
                    >
                      {translating ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Translating with prompt…</>
                      ) : (
                        <>
                          Translate →&nbsp;
                          <span className="opacity-70 font-mono text-[10px] tracking-widest">
                            {(LANGUAGES.find((l) => l.code === targetLang)?.name || targetLang).toUpperCase()}
                          </span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <TranslationPreview
                  video={video}
                  targetLang={targetLang}
                  lastTranslation={lastTranslation}
                  scriptClass={LANGUAGES.find((l) => l.code === targetLang)?.script || ""}
                  onJumpTo={jumpTo}
                  onShowOnRight={() => {
                    setBilingualView(false);
                    setDisplayLang(targetLang);
                  }}
                  onShowSideBySide={() => {
                    setDisplayLang(targetLang);
                    setBilingualView(true);
                  }}
                />
              </TabsContent>

              {/* TAB 3: Dub */}
              <TabsContent value="dub" className="mt-5 space-y-5" data-testid="panel-dub">
                <DubbingPanel video={video} api={api} API_BASE={API_BASE} onRefresh={fetchVideo} />
              </TabsContent>

              {/* TAB 4: Export */}
              <TabsContent value="export" className="mt-5" data-testid="panel-export">
                <div className="card-surface p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Download className="w-4 h-4 text-primary" />
                    <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground">
                      EXPORT · SRT · PDF · WORD
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-5">
                    Pick a primary language (and optionally a secondary for bilingual output),
                    then download in your preferred format. Indic scripts are embedded in PDFs.
                  </p>

                  <div className="grid sm:grid-cols-3 gap-3 mb-4">
                    <div>
                      <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1.5">
                        PRIMARY
                      </div>
                      <Select value={exportPrimary} onValueChange={setExportPrimary}>
                        <SelectTrigger data-testid="export-primary" className="rounded-none border-white/10 bg-secondary/60 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="original">
                            Original ({(video.language || "").toUpperCase() || "SRC"})
                          </SelectItem>
                          {LANGUAGES.filter((l) => availableTranslations.includes(l.code)).map((l) => (
                            <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1.5">
                        SECONDARY (BILINGUAL)
                      </div>
                      <Select
                        value={exportSecondary || "__none__"}
                        onValueChange={(v) => setExportSecondary(v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger data-testid="export-secondary" className="rounded-none border-white/10 bg-secondary/60 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— none —</SelectItem>
                          {exportPrimary !== "original" && (
                            <SelectItem value="original">
                              Original ({(video.language || "").toUpperCase() || "SRC"})
                            </SelectItem>
                          )}
                          {LANGUAGES.filter(
                            (l) => availableTranslations.includes(l.code) && l.code !== exportPrimary
                          ).map((l) => (
                            <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1.5">
                        SRT LAYOUT
                      </div>
                      <Select value={exportLayout} onValueChange={setExportLayout}>
                        <SelectTrigger data-testid="export-layout" className="rounded-none border-white/10 bg-secondary/60 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inline">Inline (same line)</SelectItem>
                          <SelectItem value="stacked">Stacked (line below)</SelectItem>
                          <SelectItem value="timeseries">Time series (time then text · .txt)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {video.analysis?.speakers?.length > 0 && (
                    <label className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-muted-foreground cursor-pointer mb-4">
                      <input
                        type="checkbox"
                        checked={includeSpeaker}
                        onChange={(e) => setIncludeSpeaker(e.target.checked)}
                        data-testid="include-speaker-checkbox"
                        className="accent-primary"
                      />
                      INCLUDE SPEAKER LABELS
                    </label>
                  )}

                  <label
                    className={`flex items-start gap-2 text-[10px] font-mono tracking-widest cursor-pointer mb-4 p-2 border transition-colors ${
                      exportFresh
                        ? "border-primary/50 bg-primary/5 text-primary"
                        : "border-white/10 text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={exportFresh}
                      onChange={(e) => setExportFresh(e.target.checked)}
                      data-testid="export-fresh-checkbox"
                      className="accent-primary mt-0.5"
                    />
                    <span>
                      REFINE ON DOWNLOAD ·
                      <span className="text-muted-foreground normal-case tracking-normal">
                        &nbsp;Silently re-runs the LLM on any segments still flagged
                        for review, then renders the file. Uses a few tokens per
                        flagged row — clean output guaranteed.
                      </span>
                    </span>
                  </label>

                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      onClick={() => downloadFile("srt", exportPrimary, exportSecondary)}
                      data-testid="download-srt"
                      className="rounded-none h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      SRT
                    </Button>
                    <Button
                      onClick={() => downloadFile("pdf", exportPrimary, exportSecondary)}
                      variant="outline"
                      data-testid="download-pdf"
                      className="rounded-none h-11 border-white/15 hover:border-primary/60 hover:text-primary"
                    >
                      <FileType2 className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <Button
                      onClick={() => downloadFile("docx", exportPrimary, exportSecondary)}
                      variant="outline"
                      data-testid="download-docx"
                      className="rounded-none h-11 border-white/15 hover:border-primary/60 hover:text-primary"
                    >
                      <FileType2 className="w-4 h-4 mr-2" />
                      WORD
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT: Always-visible transcript */}
          <div className="lg:col-span-5">
            <div className="card-surface flex flex-col h-[calc(100vh-13rem)] min-h-[560px] sticky top-24">
              {/* Language tabs */}
              <div className="flex items-center gap-2 p-3 border-b border-white/5 overflow-x-auto">
                <button
                  onClick={() => setDisplayLang("original")}
                  data-testid="display-lang-original"
                  disabled={editMode}
                  className={`text-[10px] font-mono tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-30 ${
                    displayLang === "original" && !bilingualView
                      ? "border-primary text-primary bg-primary/10"
                      : "border-white/10 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  ORIGINAL
                </button>
                {LANGUAGES.map((l) => {
                  const has = availableTranslations.includes(l.code) || l.code === (video.language || "en");
                  return (
                    <button
                      key={l.code}
                      onClick={() => setDisplayLang(l.code)}
                      disabled={editMode || !has}
                      data-testid={`display-lang-${l.code}`}
                      className={`text-[10px] font-mono tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                        displayLang === l.code && !bilingualView
                          ? "border-primary text-primary bg-primary/10"
                          : "border-white/10 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {l.short || l.code.toUpperCase()}
                    </button>
                  );
                })}
                {availableTranslations.length > 0 && (
                  <button
                    onClick={() => setBilingualView((v) => !v)}
                    disabled={editMode}
                    data-testid="toggle-bilingual"
                    className={`text-[10px] font-mono tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-30 ml-auto ${
                      bilingualView
                        ? "border-primary text-primary bg-primary/10"
                        : "border-white/10 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    SIDE-BY-SIDE
                  </button>
                )}
                <button
                  onClick={scrollToActive}
                  disabled={activeIndex < 0}
                  data-testid="sync-to-playback"
                  className={`text-[10px] font-mono tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                    availableTranslations.length > 0 ? "" : "ml-auto"
                  } border-white/10 text-muted-foreground hover:border-primary/50 hover:text-primary`}
                  title="Scroll to the currently playing segment"
                >
                  SYNC ▸
                </button>
              </div>

              {/* Quality review bar — only when viewing a translated language */}
              {isDone && isTranslatedView && reviewStats.hasScores && (
                <div
                  data-testid="review-bar"
                  className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/5 bg-black/30"
                >
                  <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-muted-foreground">
                    <span
                      data-testid="review-count"
                      className={
                        reviewStats.flagged > 0
                          ? "text-amber-400"
                          : "text-emerald-400"
                      }
                    >
                      {reviewStats.flagged > 0
                        ? `${reviewStats.flagged}/${reviewStats.total} NEED REVIEW`
                        : `ALL ${reviewStats.total} SEGMENTS LOOK GOOD`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => runRefine({ scope: "translation" })}
                      disabled={refining || reviewStats.flagged === 0 || editMode}
                      data-testid="refine-translation-button"
                      className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {refining ? "REFINING…" : "AI REFINE"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setReviewMode(true)}
                      disabled={reviewStats.flagged === 0 || editMode}
                      data-testid="open-review-mode"
                      className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 text-muted-foreground hover:border-amber-400/50 hover:text-amber-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      REVIEW MODE
                    </button>
                    <button
                      type="button"
                      onClick={() => setFlaggedOnly((v) => !v)}
                      disabled={reviewStats.flagged === 0 || editMode}
                      data-testid="toggle-flagged-only"
                      className={`text-[10px] font-mono tracking-widest px-2 py-1 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                        flaggedOnly
                          ? "border-amber-400 text-amber-400 bg-amber-400/10"
                          : "border-white/10 text-muted-foreground hover:border-amber-400/50 hover:text-amber-400"
                      }`}
                    >
                      {flaggedOnly ? "SHOWING FLAGGED" : "SHOW FLAGGED ONLY"}
                    </button>
                  </div>
                </div>
              )}

              {/* Edit toolbar */}
              {isDone && (
                <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 bg-black/40">
                  <div className="text-[10px] font-mono tracking-widest text-muted-foreground">
                    {editMode ? "EDITING · CLICK TEXT TO MODIFY" : "READ-ONLY"}
                  </div>
                  {editMode ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid="edit-cancel-button" className="h-7 rounded-none border border-white/10 text-xs">
                        <X className="w-3.5 h-3.5 mr-1" />Cancel
                      </Button>
                      <Button size="sm" onClick={saveEdits} disabled={savingEdits} data-testid="edit-save-button" className="h-7 rounded-none bg-primary text-primary-foreground text-xs">
                        {savingEdits ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Saving</> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={startEdit} data-testid="edit-start-button" className="h-7 rounded-none border border-white/10 hover:border-primary/50 hover:text-primary text-xs">
                      <Pencil className="w-3.5 h-3.5 mr-1" />Edit segments
                    </Button>
                  )}
                </div>
              )}

              {/* Segments */}
              <div ref={listRef} data-testid="transcript-list" className={`flex-1 overflow-y-auto ${scriptClass}`}>
                {isFailed && (
                  <div className="p-6 text-center text-destructive">
                    <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                    <div className="font-semibold">Processing failed</div>
                    <div className="text-xs mt-1 text-muted-foreground">{video.error || "Unknown error"}</div>
                  </div>
                )}
                {!isDone && !isFailed && (
                  <div className="p-8 text-center text-muted-foreground">
                    <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-primary" />
                    <div className="font-mono text-xs tracking-widest">{video.status?.toUpperCase() || "PROCESSING"}…</div>
                    <div className="text-sm mt-2">Hang tight — Whisper is transcribing your video.</div>
                  </div>
                )}
                {isDone && activeSegments.length === 0 && (
                  <div className="p-6 text-center text-muted-foreground">
                    No segments available for this language.
                  </div>
                )}
                {isDone && !editMode && bilingualView &&
                  activeSegments.map((seg, i) => {
                    if (flaggedOnly && isTranslatedView && !seg?.needs_review) return null;
                    const origSegs = video.segments || [];
                    const orig = origSegs[i]?.text || "";
                    return (
                      <button
                        key={i}
                        data-seg-idx={i}
                        data-testid={`bilingual-segment-${i}`}
                        onClick={() => jumpTo(seg.start)}
                        className={`text-left w-full p-4 border-b border-white/5 hover:bg-secondary/40 transition-colors ${
                          i === activeIndex ? "segment-active" : ""
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`timecode text-[10px] tracking-widest ${i === activeIndex ? "text-primary" : "text-muted-foreground"}`}>
                            {fmtTime(seg.start)} → {fmtTime(seg.end)}
                          </span>
                          {seg.speaker && seg.speaker !== "NA" && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 border border-primary/50 text-primary tracking-widest">
                              <Users className="w-2.5 h-2.5" />{seg.speaker}
                            </span>
                          )}
                          <QualityBadge seg={seg} testIdPrefix={`bilingual-quality-${i}`} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-[9px] font-mono tracking-widest text-muted-foreground mb-1">
                              {(video.language || "SRC").toUpperCase()}
                            </div>
                            <div className="text-sm text-foreground/80 whitespace-pre-wrap">{orig}</div>
                          </div>
                          <div className={scriptClass}>
                            <div className="text-[9px] font-mono tracking-widest text-primary mb-1">
                              {displayLang === "original" ? "ORIGINAL" : displayLang.toUpperCase()}
                            </div>
                            <div className="text-sm text-foreground whitespace-pre-wrap">{seg.text}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                {isDone && !editMode && !bilingualView &&
                  activeSegments.map((seg, i) => {
                    if (flaggedOnly && isTranslatedView && !seg?.needs_review) return null;
                    const isRetrans = retranscribeSegIdx === i;
                    const silence = silenceMap[i];
                    const inSilenceGap =
                      silence &&
                      currentTime >= (i === 0 ? 0 : activeSegments[i - 1].end) &&
                      currentTime < seg.start;
                    return (
                      <React.Fragment key={i}>
                        {silence && (
                          <div
                            data-testid={`silence-marker-${i}`}
                            className={`px-4 py-2 border-b border-dashed border-white/10 text-[10px] font-mono tracking-widest flex items-center gap-2 ${
                              inSilenceGap
                                ? "bg-primary/10 text-primary"
                                : "bg-black/40 text-muted-foreground"
                            }`}
                          >
                            <span>▸ SILENCE · {silence.toFixed(1)}s</span>
                            <span className="opacity-60">
                              ({fmtTime(i === 0 ? 0 : activeSegments[i - 1].end)} → {fmtTime(seg.start)})
                            </span>
                            <button
                              type="button"
                              onClick={() => jumpTo(seg.start)}
                              data-testid={`silence-skip-${i}`}
                              className="ml-auto px-2 py-0.5 border border-white/10 hover:border-primary/50 hover:text-primary transition-colors"
                            >
                              SKIP →
                            </button>
                          </div>
                        )}
                        <div
                          data-seg-idx={i}
                          data-testid={`transcript-segment-${i}`}
                          style={{ animationDelay: `${Math.min(i * 15, 400)}ms` }}
                          className={`fade-up group relative p-4 border-b border-white/5 hover:bg-secondary/40 transition-colors duration-150 ${
                            i === activeIndex ? "segment-active" : ""
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => jumpTo(seg.start)}
                            className="text-left w-full"
                          >
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <span className={`timecode text-[10px] tracking-widest ${i === activeIndex ? "text-primary" : "text-muted-foreground"}`}>
                                {fmtTime(seg.start)} → {fmtTime(seg.end)}
                              </span>
                              {seg.speaker && seg.speaker !== "NA" && (
                                <span data-testid={`segment-speaker-${i}`} className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 border border-primary/50 text-primary tracking-widest">
                                  <Users className="w-2.5 h-2.5" />{seg.speaker}
                                </span>
                              )}
                              {seg.lang && (
                                <span data-testid={`segment-lang-${i}`} className="text-[9px] font-mono px-1.5 py-0.5 border border-white/15 text-muted-foreground uppercase tracking-widest">
                                  {seg.lang}
                                </span>
                              )}
                              <QualityBadge seg={seg} testIdPrefix={`segment-quality-${i}`} />
                            </div>
                            <div className={`text-sm leading-relaxed whitespace-pre-wrap ${i === activeIndex ? "text-foreground" : "text-foreground/80"}`}>
                              {seg.text}
                            </div>
                          </button>
                          {!isTranslatedView && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                retranscribeSegment(i);
                              }}
                              disabled={retranscribeSegIdx != null}
                              data-testid={`redo-segment-${i}`}
                              title="Re-run Whisper on this segment's audio range"
                              className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40 text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 text-muted-foreground hover:border-primary/60 hover:text-primary bg-black/60 backdrop-blur-sm transition-all"
                            >
                              {isRetrans ? (
                                <><Loader2 className="w-3 h-3 mr-1 inline animate-spin" />REDOING</>
                              ) : (
                                <>↻ REDO</>
                              )}
                            </button>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                {isDone && editMode &&
                  editBuffer.map((seg, i) => (
                    <div key={i} data-seg-idx={i} data-testid={`edit-segment-${i}`} className="p-3 border-b border-white/5 bg-secondary/20">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <button onClick={() => jumpTo(seg.start)} className="timecode text-[10px] tracking-widest text-muted-foreground hover:text-primary">
                          {fmtTime(seg.start)} → {fmtTime(seg.end)}
                        </button>
                        {seg.speaker && seg.speaker !== "NA" && (
                          <span className="text-[9px] font-mono px-1.5 py-0.5 border border-primary/50 text-primary tracking-widest">{seg.speaker}</span>
                        )}
                      </div>
                      <Textarea
                        value={seg.text}
                        onChange={(e) => updateBufferText(i, e.target.value)}
                        data-testid={`edit-segment-text-${i}`}
                        rows={2}
                        className={`rounded-none border-white/10 bg-black/50 text-sm resize-y ${scriptClass}`}
                      />
                    </div>
                  ))}
              </div>

              {isDone && (
                <div className="border-t border-white/5 px-4 py-2.5 text-[10px] font-mono tracking-widest text-muted-foreground flex justify-between">
                  <span>{activeSegments.length} SEGMENTS</span>
                  <span>
                    {displayLang === "original"
                      ? `SOURCE · ${(video.language || "").toUpperCase()}`
                      : video.translations?.[displayLang]
                        ? `TRANSLATED · ${video.translations[displayLang].model?.toUpperCase() || ""}`
                        : displayLang.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {reviewMode && (
          <ReviewWalkthrough
            segments={activeSegments}
            origSegments={video?.segments || []}
            isTranslatedView={isTranslatedView}
            displayLang={displayLang}
            sourceLang={video?.language || "en"}
            scriptClass={scriptClass}
            onClose={() => setReviewMode(false)}
            onJumpTo={jumpTo}
            onSaveEdit={async (idx, newText) => {
              // Save single-segment edit through existing PUT /segments endpoint
              const langKey =
                isTranslatedView ? displayLang : "original";
              const src = activeSegments;
              const nextSegs = src.map((s, i) =>
                i === idx ? { ...s, text: newText } : s
              );
              try {
                await api.put(`/videos/${video.id}/segments`, {
                  lang: langKey,
                  segments: nextSegs.map((s) => ({
                    start: s.start,
                    end: s.end,
                    text: s.text,
                    speaker: s.speaker || null,
                    lang: s.lang || null,
                  })),
                });
                toast.success("Saved");
                await fetchVideo();
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            onRefineSingle={async (idx) => {
              // Trigger a targeted refine for a single flagged segment.
              // Backend refine uses `only_flagged=true` and will pick this one if flagged.
              await runRefine({ scope: isTranslatedView ? "translation" : "transcription" });
            }}
            refining={refining}
          />
        )}
      </main>
    </div>
  );
}

function StatusBar({ status, progress, error, stageStartedAt, onCancel }) {
  const cfg = {
    queued: { icon: Clock, label: "Queued", color: "text-muted-foreground border-white/10" },
    extracting: { icon: Loader2, label: "Extracting audio", color: "text-primary border-primary/40", spin: true },
    transcribing: { icon: Loader2, label: "Transcribing", color: "text-primary border-primary/40", spin: true },
    uploading: { icon: Loader2, label: "Uploading", color: "text-primary border-primary/40", spin: true },
    done: { icon: CheckCircle2, label: "Ready", color: "text-green-400 border-green-500/40" },
    failed: { icon: AlertCircle, label: "Failed", color: "text-destructive border-destructive/50" },
  }[status] || { icon: Film, label: status, color: "text-muted-foreground border-white/10" };
  const Icon = cfg.icon;

  // How long has this stage been running? If > 3 min, treat as "likely stuck"
  // and offer a Cancel escape hatch.
  const stageSeconds = stageStartedAt
    ? Math.floor((Date.now() - new Date(stageStartedAt).getTime()) / 1000)
    : 0;
  const isActive = ["extracting", "transcribing", "uploading"].includes(status);
  const looksStuck = isActive && stageSeconds > 180;

  return (
    <div className="w-full md:w-80">
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 border text-[10px] font-mono tracking-widest uppercase ${cfg.color}`}
        data-testid="video-status"
      >
        <Icon className={`w-3 h-3 ${cfg.spin ? "animate-spin" : ""}`} />
        {cfg.label}
        {isActive && stageSeconds > 10 && (
          <span className="opacity-60">· {Math.floor(stageSeconds / 60)}m {stageSeconds % 60}s</span>
        )}
      </div>
      {(status === "extracting" || status === "transcribing") && (
        <div className="mt-2 h-1 bg-secondary overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-500 glow-pulse"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {looksStuck && onCancel && (
        <div className="mt-2 flex items-center gap-2">
          <div className="text-[10px] font-mono tracking-widest text-amber-400">
            TAKING LONGER THAN USUAL
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="cancel-stuck-button"
            className="text-[10px] font-mono tracking-widest px-2 py-0.5 border border-white/15 text-muted-foreground hover:border-destructive hover:text-destructive transition-colors"
          >
            CANCEL
          </button>
        </div>
      )}
      {status === "failed" && error && (
        <div className="mt-2 text-xs text-destructive/80">{error}</div>
      )}
    </div>
  );
}


function QualityBadge({ seg, testIdPrefix }) {
  if (typeof seg?.confidence !== "number") return null;
  const pct = Math.round(seg.confidence * 100);
  const flagged = !!seg.needs_review;
  const tone = flagged
    ? "border-amber-400/60 text-amber-300 bg-amber-400/10"
    : pct >= 85
    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
    : "border-white/15 text-muted-foreground";
  const reasons = Array.isArray(seg.review_reasons) ? seg.review_reasons : [];
  return (
    <span
      data-testid={testIdPrefix}
      title={
        flagged
          ? `Needs review · ${reasons.join(", ") || "low confidence"}`
          : `Confidence ${pct}%`
      }
      className={`inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 border tracking-widest ${tone}`}
    >
      {flagged && <AlertCircle className="w-2.5 h-2.5" />}
      {flagged ? "REVIEW · " : ""}{pct}%
    </span>
  );
}



function TranslationPreview({
  video,
  targetLang,
  lastTranslation,
  scriptClass,
  onJumpTo,
  onShowOnRight,
  onShowSideBySide,
}) {
  // Priority: fresh in-memory result > persisted translation in video doc
  const stored = video?.translations?.[targetLang];
  const isFresh = lastTranslation && lastTranslation.lang === targetLang;
  const segments = isFresh
    ? lastTranslation.segments
    : stored?.segments || [];
  const model = isFresh ? lastTranslation.model : stored?.model;
  const origSegments = video?.segments || [];

  if (!segments || segments.length === 0) {
    return (
      <div
        data-testid="translate-preview-empty"
        className="card-surface p-5 border-dashed border border-white/10"
      >
        <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground mb-2">
          PREVIEW
        </div>
        <p className="text-xs text-muted-foreground">
          Run a translation above to see a side-by-side preview here. Existing
          translations for the selected target language will also appear.
        </p>
      </div>
    );
  }

  const previewSlice = segments.slice(0, 6);
  const flaggedCount = segments.reduce(
    (n, s) => n + (s?.needs_review ? 1 : 0),
    0
  );
  const hasScores = segments.some((s) => typeof s?.confidence === "number");

  return (
    <div className="card-surface p-5" data-testid="translate-preview">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary" />
          <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground">
            PREVIEW · {targetLang.toUpperCase()}
            {model ? ` · ${model.toUpperCase()}` : ""}
          </div>
          {hasScores && (
            <span
              data-testid="translate-preview-review-count"
              className={`text-[10px] font-mono tracking-widest px-2 py-0.5 border ${
                flaggedCount > 0
                  ? "border-amber-400/60 text-amber-300 bg-amber-400/10"
                  : "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
              }`}
            >
              {flaggedCount > 0
                ? `${flaggedCount}/${segments.length} REVIEW`
                : `${segments.length} OK`}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onShowOnRight}
            data-testid="preview-show-right"
            className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
          >
            OPEN IN PANEL →
          </button>
          <button
            type="button"
            onClick={onShowSideBySide}
            data-testid="preview-show-bilingual"
            className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
          >
            SIDE-BY-SIDE →
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Showing {previewSlice.length} of {segments.length} translated segments.
        Click a row to jump the video to that timecode.
      </p>
      <div className="divide-y divide-white/5 border border-white/5">
        {previewSlice.map((seg, i) => {
          const orig = origSegments[i]?.text || "";
          return (
            <button
              key={i}
              type="button"
              data-testid={`translate-preview-row-${i}`}
              onClick={() => onJumpTo(seg.start)}
              className="w-full text-left p-3 hover:bg-secondary/40 transition-colors"
            >
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="timecode text-[10px] tracking-widest text-muted-foreground">
                  {fmtTime(seg.start)} → {fmtTime(seg.end)}
                </span>
                <QualityBadge seg={seg} testIdPrefix={`translate-preview-quality-${i}`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[9px] font-mono tracking-widest text-muted-foreground mb-1">
                    {(video?.language || "SRC").toUpperCase()}
                  </div>
                  <div className="text-sm text-foreground/70 whitespace-pre-wrap">
                    {orig}
                  </div>
                </div>
                <div className={scriptClass}>
                  <div className="text-[9px] font-mono tracking-widest text-primary mb-1">
                    {targetLang.toUpperCase()}
                  </div>
                  <div className="text-sm text-foreground whitespace-pre-wrap">
                    {seg.text}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {segments.length > previewSlice.length && (
        <div className="mt-2 text-right text-[10px] font-mono tracking-widest text-muted-foreground">
          + {segments.length - previewSlice.length} MORE · OPEN IN PANEL FOR FULL LIST
        </div>
      )}
    </div>
  );
}


function ReviewWalkthrough({
  segments,
  origSegments,
  isTranslatedView,
  displayLang,
  sourceLang,
  scriptClass,
  onClose,
  onJumpTo,
  onSaveEdit,
  onRefineSingle,
  refining,
}) {
  // Build ordered list of flagged indexes so we walk in original order
  const flaggedIndexes = useMemo(
    () => segments.map((s, i) => (s?.needs_review ? i : -1)).filter((i) => i >= 0),
    [segments]
  );
  const [cursor, setCursor] = useState(0);
  const [draft, setDraft] = useState("");

  const currentIdx = flaggedIndexes[cursor];
  const seg = currentIdx != null ? segments[currentIdx] : null;

  useEffect(() => {
    setDraft(seg?.text || "");
  }, [seg]);

  if (!seg) {
    return (
      <div
        data-testid="review-modal-empty"
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
        onClick={onClose}
      >
        <div className="card-surface p-8 max-w-md text-center" onClick={(e) => e.stopPropagation()}>
          <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-emerald-400" />
          <div className="font-black text-lg tracking-tight mb-1">All clear</div>
          <div className="text-xs text-muted-foreground mb-5">
            No segments are currently flagged for review.
          </div>
          <Button onClick={onClose} data-testid="review-modal-close" className="rounded-none">
            Close
          </Button>
        </div>
      </div>
    );
  }

  const origText = origSegments[currentIdx]?.text || "";
  const reasons = Array.isArray(seg.review_reasons) ? seg.review_reasons : [];
  const pct = typeof seg.confidence === "number" ? Math.round(seg.confidence * 100) : null;

  const goPrev = () => setCursor((c) => Math.max(0, c - 1));
  const goNext = () => setCursor((c) => Math.min(flaggedIndexes.length - 1, c + 1));

  return (
    <div
      data-testid="review-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 lg:p-8"
      onClick={onClose}
    >
      <div
        className="card-surface w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400" />
            <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground">
              REVIEW MODE · {isTranslatedView ? displayLang.toUpperCase() : "TRANSCRIPT"} ·
              &nbsp;{cursor + 1}/{flaggedIndexes.length}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="review-modal-close"
            className="text-muted-foreground hover:text-foreground p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onJumpTo(seg.start)}
              data-testid="review-modal-jump"
              className="timecode text-[10px] tracking-widest text-primary hover:underline"
            >
              {fmtTime(seg.start)} → {fmtTime(seg.end)}
            </button>
            {pct != null && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 border border-amber-400/60 text-amber-300 bg-amber-400/10 tracking-widest">
                REVIEW · {pct}%
              </span>
            )}
            {reasons.map((r) => (
              <span
                key={r}
                data-testid={`review-reason-${r}`}
                className="text-[9px] font-mono px-1.5 py-0.5 border border-white/15 text-muted-foreground tracking-widest"
              >
                {r.replace(/_/g, " ").toUpperCase()}
              </span>
            ))}
          </div>

          {isTranslatedView && (
            <div>
              <div className="text-[9px] font-mono tracking-widest text-muted-foreground mb-1">
                {sourceLang.toUpperCase()} · SOURCE
              </div>
              <div className="text-sm text-foreground/70 whitespace-pre-wrap border border-white/5 p-3 bg-black/30">
                {origText}
              </div>
            </div>
          )}

          <div>
            <div className="text-[9px] font-mono tracking-widest text-primary mb-1">
              {(isTranslatedView ? displayLang : sourceLang).toUpperCase()} · EDIT
            </div>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid="review-modal-edit"
              rows={4}
              className={`rounded-none border-white/15 bg-black/50 text-sm resize-y ${scriptClass}`}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-t border-white/10">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={goPrev}
              disabled={cursor === 0}
              data-testid="review-modal-prev"
              className="rounded-none border-white/15"
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={goNext}
              disabled={cursor === flaggedIndexes.length - 1}
              data-testid="review-modal-next"
              className="rounded-none border-white/15"
            >
              Next →
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRefineSingle(currentIdx)}
              disabled={refining}
              data-testid="review-modal-refine"
              className="rounded-none border-white/15 hover:border-primary/50 hover:text-primary"
            >
              {refining ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />AI Retry</>
              ) : (
                <><Wand2 className="w-3.5 h-3.5 mr-1" />AI Retry all flagged</>
              )}
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                if (draft.trim() && draft !== seg.text) {
                  await onSaveEdit(currentIdx, draft.trim());
                }
                goNext();
              }}
              data-testid="review-modal-save-next"
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
            >
              <Save className="w-3.5 h-3.5 mr-1" />Save & Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DubbingPanel({ video, api, API_BASE, onRefresh }) {
  const availableTranslations = Object.keys(video?.translations || {});
  const [targetLang, setTargetLang] = useState(availableTranslations[0] || "");
  const [voices, setVoices] = useState({ preferred: [], fallback: [] });
  const [voiceMap, setVoiceMap] = useState({});
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(false);

  const dubs = video?.dubs || {};
  const currentDub = targetLang ? dubs[targetLang] : null;

  // Load voice list once
  useEffect(() => {
    api.get("/dubbing/voices").then((r) => setVoices(r.data)).catch(() => {});
  }, [api]);

  // Reset default voice map when target changes
  useEffect(() => {
    if (!targetLang || !voices.preferred?.length) return;
    const origSegs = video?.segments || [];
    const speakers = [];
    origSegs.forEach((s) => {
      const sp = s?.speaker || "S1";
      if (!speakers.includes(sp)) speakers.push(sp);
    });
    // Preserve existing persisted voice_map if present
    const stored = dubs[targetLang]?.voice_map || {};
    const map = {};
    speakers.forEach((sp, i) => {
      map[sp] = stored[sp] || voices.preferred[i % voices.preferred.length];
    });
    setVoiceMap(map);
  }, [targetLang, voices.preferred, video, dubs]);

  // Poll while dub is in progress
  useEffect(() => {
    if (!currentDub) return;
    const inProgress = ["queued", "synthesizing", "muxing"].includes(currentDub.status);
    if (!inProgress) {
      setPolling(false);
      setStarting(false);
      return;
    }
    setPolling(true);
    const t = setInterval(() => onRefresh?.(), 4000);
    return () => clearInterval(t);
  }, [currentDub?.status, onRefresh]);

  const runDub = async () => {
    if (!targetLang) return;
    setStarting(true);
    try {
      await api.post(`/videos/${video.id}/dub`, { target_lang: targetLang, voice_map: voiceMap });
      toast.success(`Dubbing started → ${targetLang.toUpperCase()}`);
      await onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setStarting(false);
    }
  };

  const token = typeof window !== "undefined" ? localStorage.getItem("vt_token") || "" : "";
  const audioUrl = currentDub?.status === "done"
    ? `${API_BASE}/videos/${video.id}/dub/${targetLang}/audio?token=${encodeURIComponent(token)}`
    : null;
  const videoUrl = currentDub?.status === "done" && currentDub?.video_path
    ? `${API_BASE}/videos/${video.id}/dub/${targetLang}/video?token=${encodeURIComponent(token)}`
    : null;

  if (availableTranslations.length === 0) {
    return (
      <div className="card-surface p-8 text-center">
        <Volume2 className="w-6 h-6 mx-auto mb-3 text-muted-foreground" />
        <div className="text-sm text-muted-foreground mb-2">No translations yet.</div>
        <div className="text-xs text-muted-foreground">
          Translate the video to at least one language first — dubbing uses the translated segments.
        </div>
      </div>
    );
  }

  const speakers = Object.keys(voiceMap);
  const inProgress = currentDub && ["queued", "synthesizing", "muxing"].includes(currentDub.status);

  return (
    <div className="card-surface p-5" data-testid="dub-panel">
      <div className="flex items-center gap-2 mb-4">
        <Volume2 className="w-4 h-4 text-primary" />
        <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground">
          DUB · SARVAM BULBUL v3
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1.5">
            TARGET LANGUAGE
          </div>
          <Select value={targetLang} onValueChange={setTargetLang}>
            <SelectTrigger data-testid="dub-target-lang" className="rounded-none border-white/10 bg-secondary/60 h-10">
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {availableTranslations.map((code) => (
                <SelectItem key={code} value={code}>
                  {LANGUAGES.find((l) => l.code === code)?.name || code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {speakers.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-2">
            VOICE PER SPEAKER
          </div>
          <div className="space-y-2">
            {speakers.map((sp) => (
              <div key={sp} className="flex items-center gap-3">
                <div
                  className="inline-flex items-center gap-1 text-[9px] font-mono px-2 py-1 border border-primary/50 text-primary tracking-widest min-w-[80px]"
                  data-testid={`dub-speaker-${sp}`}
                >
                  <Users className="w-2.5 h-2.5" /> {sp}
                </div>
                <Select
                  value={voiceMap[sp]}
                  onValueChange={(v) => setVoiceMap((m) => ({ ...m, [sp]: v }))}
                >
                  <SelectTrigger
                    data-testid={`dub-voice-${sp}`}
                    className="rounded-none border-white/10 bg-secondary/60 h-9 flex-1 max-w-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(voices.fallback || []).map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={runDub}
        disabled={!targetLang || starting || inProgress}
        data-testid="dub-start-button"
        className="rounded-none h-12 w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-sm tracking-wide"
      >
        {starting || inProgress ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{currentDub?.status?.toUpperCase() || "Starting"}…</>
        ) : (
          <>Dub → {(LANGUAGES.find((l) => l.code === targetLang)?.name || targetLang).toUpperCase()}</>
        )}
      </Button>

      {currentDub && (
        <div className="mt-5 border border-white/5 p-4 bg-black/30" data-testid="dub-status">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono tracking-widest text-muted-foreground">
              STATUS
            </div>
            <div
              className={`text-[10px] font-mono tracking-widest ${
                currentDub.status === "done" ? "text-emerald-400"
                : currentDub.status === "failed" ? "text-red-400"
                : "text-primary"
              }`}
            >
              {currentDub.status?.toUpperCase()} · {currentDub.progress || 0}%
            </div>
          </div>
          {currentDub.error && (
            <div className="text-xs text-red-300 mb-2">{currentDub.error}</div>
          )}
          {currentDub.status === "done" && (
            <div className="space-y-3">
              {audioUrl && (
                <div>
                  <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1">
                    DUBBED AUDIO
                  </div>
                  <audio
                    src={audioUrl}
                    controls
                    data-testid="dub-audio-player"
                    className="w-full"
                  />
                  <a
                    href={audioUrl}
                    download
                    data-testid="dub-audio-download"
                    className="inline-block mt-1 text-[10px] font-mono tracking-widest text-primary hover:underline"
                  >
                    ↓ DOWNLOAD .WAV
                  </a>
                </div>
              )}
              {videoUrl && (
                <div>
                  <div className="text-[10px] font-mono tracking-widest text-muted-foreground mb-1">
                    DUBBED VIDEO
                  </div>
                  <video
                    src={videoUrl}
                    controls
                    data-testid="dub-video-player"
                    className="w-full max-h-72 bg-black"
                  />
                  <a
                    href={videoUrl}
                    download
                    data-testid="dub-video-download"
                    className="inline-block mt-1 text-[10px] font-mono tracking-widest text-primary hover:underline"
                  >
                    ↓ DOWNLOAD .MP4
                  </a>
                </div>
              )}
            </div>
          )}
          {polling && (
            <div className="text-[10px] font-mono tracking-widest text-muted-foreground">
              Polling for status every 4s…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

